import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Receipt, Download, CheckCircle2, X, DollarSign, Loader2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import { onsiteApiFetch } from '../config/api.js';
import { formatDate, cn } from '../lib/utils.js';

interface InvoiceLineItem {
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

interface OnsiteInvoice {
  id: string;
  invoice_number: string;
  job_id: string;
  job_title: string;
  client_id?: string;
  client_name: string;
  issued_at: string;
  status: 'draft' | 'sent' | 'paid';
  line_items: InvoiceLineItem[];
  subtotal: number;
  tax: number;
  total: number;
  paid_at?: string;
  notes?: string;
}

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'bg-yellow-500/20 text-yellow-400' },
  sent:  { label: 'Sent',  color: 'bg-blue-500/20 text-blue-400'    },
  paid:  { label: 'Paid',  color: 'bg-green-500/20 text-green-400'  },
};

export default function InvoicesPage() {
  const { orgId } = useAuth();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<OnsiteInvoice | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['invoices', orgId, statusFilter],
    queryFn: async () => {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      try {
        return await onsiteApiFetch<{ invoices: OnsiteInvoice[] }>(`/api/v1/onsite/billing/invoices${qs}`);
      } catch (e) {
        // Endpoint may not exist yet — treat 404 as empty
        const msg = (e as Error).message;
        if (msg.toLowerCase().includes('not found') || msg.includes('404')) {
          return { invoices: [] };
        }
        throw e;
      }
    },
    enabled: !!orgId,
  });

  const markPaid = useMutation({
    mutationFn: (invoiceId: string) =>
      onsiteApiFetch(`/api/v1/onsite/billing/invoices/${invoiceId}/mark-paid`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  });

  const sendInvoice = useMutation({
    mutationFn: (invoiceId: string) =>
      onsiteApiFetch(`/api/v1/onsite/billing/invoices/${invoiceId}/send`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  });

  const invoices = data?.invoices ?? [];

  const totalRevenue = useMemo(
    () => invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.total ?? 0), 0),
    [invoices]
  );
  const outstanding = useMemo(
    () => invoices.filter(i => i.status === 'sent').reduce((sum, i) => sum + (i.total ?? 0), 0),
    [invoices]
  );

  function exportCsv() {
    if (invoices.length === 0) return;
    const header = ['Invoice #', 'Job', 'Client', 'Date', 'Status', 'Subtotal', 'Tax', 'Total'];
    const rows = invoices.map(inv => [
      inv.invoice_number,
      inv.job_title,
      inv.client_name,
      formatDate(inv.issued_at),
      inv.status,
      (inv.subtotal ?? 0).toFixed(2),
      (inv.tax ?? 0).toFixed(2),
      (inv.total ?? 0).toFixed(2),
    ]);
    const csv = [header, ...rows].map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoices-${new Date().toISOString().substring(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border bg-card p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold text-foreground">
              <Receipt className="h-5 w-5 text-primary" />
              Invoices
            </h1>
            <p className="text-xs text-muted-foreground">Track billing for completed jobs</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
            </select>
            <button
              onClick={exportCsv}
              disabled={invoices.length === 0}
              className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <KpiCard label="Total Revenue" value={`$${totalRevenue.toFixed(2)}`} accent="text-green-400" />
          <KpiCard label="Outstanding" value={`$${outstanding.toFixed(2)}`} accent="text-amber-400" />
          <KpiCard label="Invoices" value={invoices.length.toString()} accent="text-primary" />
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground">
            <Receipt className="h-10 w-10" />
            <p className="font-medium text-foreground">Could not load invoices</p>
            <p className="text-sm text-red-400">{(error as Error).message}</p>
          </div>
        ) : invoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground">
            <Receipt className="h-12 w-12" />
            <p className="text-lg font-medium text-foreground">No invoices yet</p>
            <p className="text-sm text-center max-w-xs">
              Complete a job to generate your first invoice.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-card">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Invoice #</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Job</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden sm:table-cell">Client</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell">Date</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden lg:table-cell">Line Items</th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">Total</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => {
                  const status = STATUS_BADGE[inv.status] ?? STATUS_BADGE.draft;
                  const lineSummary = inv.line_items.length === 1
                    ? inv.line_items[0].description
                    : `${inv.line_items.length} items`;
                  return (
                    <tr key={inv.id} className="border-b border-border last:border-0 hover:bg-secondary/50">
                      <td className="px-4 py-3 font-mono text-foreground">{inv.invoice_number}</td>
                      <td className="px-4 py-3 text-foreground max-w-xs truncate">{inv.job_title}</td>
                      <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{inv.client_name}</td>
                      <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{formatDate(inv.issued_at)}</td>
                      <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell max-w-xs truncate">{lineSummary}</td>
                      <td className="px-4 py-3 text-right font-semibold text-foreground">${inv.total.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', status.color)}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setSelected(inv)}
                          className="rounded px-2 py-1 text-xs text-primary hover:bg-primary/10"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail slide-over */}
      {selected && (
        <InvoiceDetailModal
          invoice={selected}
          onClose={() => setSelected(null)}
          onMarkPaid={async () => {
            await markPaid.mutateAsync(selected.id);
            setSelected(null);
          }}
          onSend={async () => {
            await sendInvoice.mutateAsync(selected.id);
            setSelected(null);
          }}
          markingPaid={markPaid.isPending}
          sending={sendInvoice.isPending}
        />
      )}
    </div>
  );
}

function KpiCard({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className={cn('mb-1', accent)}>
        <DollarSign className="h-5 w-5" />
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function InvoiceDetailModal({ invoice, onClose, onMarkPaid, onSend, markingPaid, sending }: {
  invoice: OnsiteInvoice;
  onClose: () => void;
  onMarkPaid: () => void;
  onSend: () => void;
  markingPaid: boolean;
  sending: boolean;
}) {
  const status = STATUS_BADGE[invoice.status] ?? STATUS_BADGE.draft;
  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/50" onClick={onClose}>
      <div
        className="w-full max-w-xl bg-background border-l border-border overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Invoice {invoice.invoice_number}</h2>
            <p className="text-xs text-muted-foreground">Issued {formatDate(invoice.issued_at)}</p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex items-center gap-2">
            <span className={cn('rounded-full px-2.5 py-1 text-xs font-medium', status.color)}>
              {status.label}
            </span>
            {invoice.paid_at && (
              <span className="text-xs text-muted-foreground">Paid {formatDate(invoice.paid_at)}</span>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-3 space-y-1">
            <Row label="Job" value={invoice.job_title} />
            <Row label="Client" value={invoice.client_name} />
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-foreground">Line Items</h3>
            {invoice.line_items.length === 0 ? (
              <p className="text-sm text-muted-foreground">No line items on this invoice.</p>
            ) : (
              <div className="rounded-xl border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-card border-b border-border">
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Description</th>
                      <th className="px-3 py-2 text-right font-medium text-muted-foreground">Qty</th>
                      <th className="px-3 py-2 text-right font-medium text-muted-foreground">Unit</th>
                      <th className="px-3 py-2 text-right font-medium text-muted-foreground">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.line_items.map((li, i) => (
                      <tr key={i} className="border-b border-border last:border-0">
                        <td className="px-3 py-2 text-foreground">{li.description}</td>
                        <td className="px-3 py-2 text-right text-muted-foreground">{li.quantity}</td>
                        <td className="px-3 py-2 text-right text-muted-foreground">${li.unit_price.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right font-medium text-foreground">${li.total.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-3 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="text-foreground">${(invoice.subtotal ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tax</span>
              <span className="text-foreground">${(invoice.tax ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between pt-1 border-t border-border">
              <span className="font-semibold text-foreground">Total</span>
              <span className="font-bold text-green-400">${invoice.total.toFixed(2)}</span>
            </div>
          </div>

          {invoice.notes && (
            <div className="rounded-xl border border-border bg-card p-3">
              <p className="text-xs text-muted-foreground mb-1">Notes</p>
              <p className="text-sm text-foreground">{invoice.notes}</p>
            </div>
          )}

          {invoice.status === 'draft' && (
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={onSend}
                disabled={sending || markingPaid}
                className="rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />}
                Send Invoice
              </button>
              <button
                onClick={onMarkPaid}
                disabled={markingPaid || sending}
                className="rounded-xl bg-green-600 px-4 py-3 text-sm font-semibold text-white hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {markingPaid ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Mark Paid
              </button>
            </div>
          )}
          {invoice.status === 'sent' && (
            <button
              onClick={onMarkPaid}
              disabled={markingPaid}
              className="w-full rounded-xl bg-green-600 px-4 py-3 text-sm font-semibold text-white hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {markingPaid ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Mark as Paid
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground text-right">{value}</span>
    </div>
  );
}
